<template>
  <div
    class="main-wishlist critical-component-clear container"
    :class="{
      [$class('loading')]: !hydrated
    }"
  >
    <div class="main-wishlist__header">
      <h1
        v-if="title"
        :id="`section-title-${liquid.sectionId}`"
        class="main-wishlist__title text-heading-s"
        v-html="title"
      />

      <p
        v-if="subheading"
        ref="subheading"
        class="main-wishlist__subheading text-body-s-regular"
        v-html="subheading"
      />

      <ul
        v-if="wishlist.publicId"
        class="main-wishlist__share text-body-s-regular"
      >
        <li
          v-if="$string('wishlist_page.share')"
          class="main-wishlist__share-label"
          v-text="$string('wishlist_page.share')"
        />
        <li>
          <a
            :href="shareLinks.sms"
            :aria-label="$string('wishlist_page.accessibility.sms')"
            @click="trackShare('sms')"
          >
            <icon-chat />
          </a>
        </li>
        <li class="main-wishlist__whatsapp">
          <a
            :href="shareLinks.whatsapp"
            data-action="share/whatsapp/share"
            :aria-label="$string('wishlist_page.accessibility.whatsapp')"
            @click="trackShare('whatsapp')"
          >
            <icon-whatsapp />
          </a>
        </li>
        <li>
          <a
            :href="shareLinks.email"
            :aria-label="$string('wishlist_page.accessibility.email')"
            target="_blank"
            rel="noopener noreferrer"
            @click="trackShare('email')"
          >
            <icon-email />
          </a>
        </li>
        <li>
          <button
            type="button"
            class="button--reset"
            :aria-label="$string('wishlist_page.accessibility.copy')"
            :disabled="copied"
            @click="copyWishlist"
          >
            <template v-if="copied">
              <icon-check />
              {{ $string('accessibility.copied') }}
            </template>
            <icon-link v-else />
          </button>
        </li>
      </ul>
    </div>

    <div class="main-wishlist__items grid">
      <template v-if="!isEmpty">
        <div
          v-for="product of products"
          :key="product.id"
          class="col xs3 l3 main-wishlist__item"
        >
          <product-card
            class="col xs3 l3"
            :product="product"
            :wishlist="false"
            quick-add
            :variant-id="product.wishlistVariantId"
            source="wishlist"
            @update-variant="payload => updateWishlistVariant(product, payload)"
            @add-cart="trackAddToCart"
          />

          <button
            v-if="wishlist.isMine && hydrated"
            type="button"
            class="button--reset main-wishlist__remove"
            :aria-label="$string('wishlist_page.accessibility.remove', {replace: {
              title: product.title
            }})"
            @click="removeItem(product.wishlistId)"
          >
            <icon-close />
          </button>
        </div>
      </template>

      <div
        v-else-if="liquid.emptyUrl"
        class="main-wishlist__empty col xs6 l4"
      >
        <btn
          :url="liquid.emptyUrl"
          :label="$string('wishlist_page.empty.continue_shopping')"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Wishlist page (main-wishlist)
 * -----------------------------------------------------------------------------
 * Displays wishlist items as product cards.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import ProductCard from '~async/product-card/product-card'
import Btn from '~global/btn/btn'

import IconChat from '~icons/general/chat.svg'
import IconCheck from '~icons/general/check.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconEmail from '~icons/general/email-alt.svg'
import IconLink from '~icons/general/link.svg'
import IconWhatsapp from '~icons/social/whatsapp.svg'

import productsQuery from '~graphql/products.gql'

import { formatProduct } from '~/helpers/format'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatEcommerceEvent, track } from '~/helpers/datalayer-actions'

export default {
  name: 'MainWishlist',

  components: {
    Btn,
    IconChat,
    IconCheck,
    IconClose,
    IconEmail,
    IconLink,
    IconWhatsapp,
    ProductCard,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        shareText: '',
        emptyUrl: '',
      }),
    },
    wishlistId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      copied: false,
      wishlist: {
        id: this.wishlistId,
        isMine: this.wishlistId === 'mine',
        items: [],
        numItems: 0,
        publicId: this.wishlistId === 'mine' ? '' : this.wishlistId,
      },
      productCache: {},
      hydrated: false,
      stringOpts: {
        replace: {
          login_url: `${cnvs.routes.account.login}?return_url=${cnvs.routes.wishlist}`,
          register_url: `${cnvs.routes.account.register}?return_url=${cnvs.routes.wishlist}`,
          customer: cnvs.customer?.firstName || '',
        },
      },
    }
  },

  computed: {

    /**
     * Compute if wishlist is empty
     * @returns {boolean}
     */
    isEmpty() {
      return this.hydrated && this.wishlist.numItems === 0
    },

    /**
     * Compute share links and messages
     * @returns {{whatsapp: string, sms: string, url: string, email: string}}
     */
    shareLinks() {
      const id = this.wishlist.publicId
      const url = new URL(`${this.$variable('routes.wishlist')}/${id}`, window.origin)
      const text = `${this.liquid.shareText} ${url.href}`.trim()

      return {
        whatsapp: `whatsapp://send?text=${text}`,
        sms: `sms:?&body=${text}`,
        url: url.href,
        email: `mailto:?body=${text}`,
      }
    },

    /**
     * Compute subheading
     * @returns {String}
     */
    subheading() {
      const isGuest = !this.$variable('customer.loggedIn')

      if (!this.wishlist.isMine) {
        if (this.isEmpty) {
          return this.$string('wishlist_page.empty.shared_subheading_html', this.stringOpts)
        }

        return ''
      }

      if (isGuest) {
        return this.$string('wishlist_page.guest.subheading_html', this.stringOpts)
      }

      return this.isEmpty
        ? this.$string('wishlist_page.empty.subheading_html', this.stringOpts)
        : this.$string('wishlist_page.logged_in.subheading_html', this.stringOpts)
    },

    /**
     * Compute title
     * @returns {string}
     */
    title() {
      return this.wishlist.isMine && this.$variable('customer.loggedIn')
        ? this.$string('wishlist_page.logged_in.title', this.stringOpts)
        : this.$string('wishlist_page.guest.title', this.stringOpts)
    },

    /**
     * Compute wishlist products from cache
     * @returns {{}}
     */
    products() {
      const products = {}

      /**
       * Maintain skeleton if no wishlist items
       */
      if (!this.wishlist.items.length || !Object.keys(this.productCache).length) {
        Array.from(Array(8).keys()).forEach((key) => {
          products[key] = false
        })

        return products
      }

      const items = this.wishlist.items.slice().reverse()

      items.forEach((item) => {
        const product = this.productCache[item.product.id]
        if (!product) {
          return
        }
        products[product.id] = {
          ...product,
          wishlistId: item.id,
          wishlistVariantId: item.variantId,
        }
      })

      return products
    },
  },

  watch: {

    /**
     * Watch for subheading changes to re-attach analytic listeners
     */
    subheading() {
      this.setSubheadingListeners()
    },

    /**
     * Get products from storefront API if not
     * already cached when wishlist items change.
     * @returns {Promise<void>}
     */
    async 'wishlist.items'() {
      let response = null
      const ids = []
      this.wishlist.items.forEach(({ product }) => {
        if (this.productCache[product.id]) {
          return
        }
        ids.push(`gid://shopify/Product/${product.id}`)
      })

      if (!ids.length) {
        return
      }

      try {
        response = await cnvs.Query({
          query: productsQuery,
          variables: getVariables({
            ids,
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        const cache = { ...this.productCache }

        response.nodes.forEach((product) => {
          const formatted = formatProduct({ product })
          if (!formatted) {
            return
          }
          cache[formatted.id] = formatted
        })

        this.productCache = cache
      } catch (error) {
        cnvs.ReportError('[Wishlist - Product Query]', error)
        cnvs.ReportError('[Wishlist - Product Query Response]', response)
      }
    },
  },

  mounted() {
    this.setSubheadingListeners()
  },

  created() {
    this.setEventListeners()
    this.initWishlist()
  },

  methods: {
    track,

    /**
     * Copy wishlist to clipboard and set copied feedback
     */
    copyWishlist() {
      navigator.clipboard.writeText(this.shareLinks.url)
      this.copied = true

      this.trackShare('copy')

      setTimeout(() => {
        this.copied = false
      }, this.$timing('message'))
    },

    /**
     * Assign wishlist event data to reactive data
     * @param {CustomEvent} event
     */
    async handleWishlistEvent(event) {
      const wishlist = event?.detail?.wishlist
      if (!wishlist) {
        return
      }

      if (wishlist.isMine) {
        sessionStorage.setItem('wishlist-cache', JSON.stringify(wishlist))
      }

      this.wishlist = { ...wishlist }
      await this.$nextTick()

      if (!this.hydrated) {
        this.emitProductUpdate()
      }

      this.hydrated = true
    },

    /**
     * Initialize wishlist from Wishlist King (if the app loaded before Vue)
     * or cache. If there is no cache and WK is not ready, force hydrate
     * within 5 seconds.
     */
    async initWishlist() {
      const wishlistCache = JSON.parse(sessionStorage.getItem('wishlist-cache'))
      const wkList = this.$variable('wk.wishlist')

      if (wkList) {
        this.wishlist = wkList
      } else if (this.wishlist.isMine && wishlistCache) {
        this.wishlist = wishlistCache
      } else {
        await cnvs.Sleep(5 * 1000)
      }

      if (!this.hydrated) {
        this.emitProductUpdate()
      }

      this.hydrated = true
    },

    /**
     * Emit user's latest wishlisted product ID from cache.
     * Cache belongs to wishlist owner.
     */
    emitProductUpdate() {
      try {
        const items = JSON.parse(sessionStorage.getItem('wishlist-cache'))?.items || []

        if (!items.length) {
          return
        }

        cnvs.EventBus.emit('main-product-update', {
          id: items[items.length - 1].product.id,
        })
      } catch {

        /* empty */
      }
    },

    /**
     * Set event listeners
     */
    setEventListeners() {
      cnvs.EventBus.listen('main-product-request', this.emitProductUpdate)
      document.addEventListener('wk:cnvs:update', this.handleWishlistEvent)
    },

    /**
     * Remove item from wishlist
     * @param {String} id - Wishlist item ID
     */
    removeItem(id) {
      const { WishlistKing } = window
      if (!this.wishlist.isMine || !id) {
        return
      }

      WishlistKing.removeWishlistItem({ wishlistItemId: id }).catch()
    },

    /**
     * Attach listeners to links in subheading for wishlist analytics
     */
    setSubheadingListeners() {
      if (!this.$refs.subheading) {
        return
      }
      this.$refs.subheading.querySelectorAll('a').forEach((anchor) => {
        anchor.removeEventListener('click', this.trackWishlistLogin)
        anchor.addEventListener('click', this.trackWishlistLogin)
      })
    },

    /**
     * Update selected variant in wishlist
     * @param product
     * @param variant
     */
    updateWishlistVariant(product, variant) {
      const { WishlistKing } = window
      if (
        !this.hydrated ||
        !this.wishlist.isMine ||
        !product.wishlistId ||
        !variant ||
        !WishlistKing?._loaded
      ) {
        return
      }

      WishlistKing.updateWishlistItem({
        wishlistItemId: product.wishlistId,
        changes: {
          variantId: variant.id,
        },
      }).catch()
    },

    /**
     * Track add to cart
     * @param payload
     */
    trackAddToCart(payload) {
      track({
        event: 'wishlist_add_to_cart',
        ...formatEcommerceEvent(payload),
      })
    },

    /**
     * Track wishlist share
     * @param {String} action
     */
    trackShare(action) {
      track({
        event: 'wishlist_share',
        action,
      })
    },

    /**
     * Detect if a vistor logs in or registers for an account from wishlist page
     * @param event - Clicked anchor element
     */
    trackWishlistLogin(event) {
      const target = event.currentTarget
      const url = target.href || ''

      if (target.dataset.tracked) {
        return
      }

      let type = ''

      if (url.includes(this.$variable('routes.account.login'))) {
        type = 'login'
      }

      if (url.includes(this.$variable('routes.account.register'))) {
        type = 'register'
      }

      if (!type) {
        return
      }

      target.dataset.tracked = 'true'
      track({
        event: 'wishlist_login',
        type,
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-wishlist';
</style>
